import React, { useState, useEffect, useRef } from 'react';
import { X, CreditCard, Shield, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import api from '../../services/api';

const CONSENT_TEXT = `I authorize this law firm to securely store my payment card information and to charge this card for:

• Retainer replenishment when my account balance falls below the specified threshold
• Scheduled recurring payments as agreed upon

I understand that:
• I can cancel this authorization at any time
• I will receive email receipts for all charges
• My card information is securely stored by Square and is never stored on the firm's servers

By clicking "Authorize", I consent to these terms.`;

function PaymentAuthorizationModal({ isOpen, onClose, contactId, caseId = null, onSuccess }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [squareConfig, setSquareConfig] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [cardholderName, setCardholderName] = useState('');
  const [authType, setAuthType] = useState('both');

  const cardContainerRef = useRef(null);
  const cardRef = useRef(null);
  const paymentsRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      loadSquareConfig();
    }
    return () => {
      // Cleanup Square card on unmount
      if (cardRef.current) {
        cardRef.current.destroy();
        cardRef.current = null;
      }
    };
  }, [isOpen]);

  const loadSquareConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/recurring-payments/square-config');
      setSquareConfig(response.data);

      // Load Square Web Payments SDK
      await loadSquareSDK(response.data);
    } catch (err) {
      console.error('Failed to load Square config:', err);
      setError('Failed to initialize payment form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadSquareSDK = async (config) => {
    // Check if SDK is already loaded
    if (window.Square) {
      await initializeCard(config);
      return;
    }

    // Load Square Web Payments SDK
    const script = document.createElement('script');
    script.src = config.environment === 'production'
      ? 'https://web.squarecdn.com/v1/square.js'
      : 'https://sandbox.web.squarecdn.com/v1/square.js';
    script.async = true;
    script.onload = () => initializeCard(config);
    script.onerror = () => setError('Failed to load payment SDK');
    document.body.appendChild(script);
  };

  const initializeCard = async (config) => {
    try {
      if (!window.Square) {
        setError('Payment SDK not loaded');
        return;
      }

      const payments = window.Square.payments(config.application_id, config.location_id);
      paymentsRef.current = payments;

      const card = await payments.card();
      cardRef.current = card;

      // Wait for container to be available
      setTimeout(async () => {
        if (cardContainerRef.current) {
          await card.attach('#card-container');

          card.addEventListener('focusClassAdded', () => {});
          card.addEventListener('focusClassRemoved', () => {});
          card.addEventListener('errorClassAdded', () => setCardComplete(false));
          card.addEventListener('errorClassRemoved', () => {});
          card.addEventListener('cardBrandChanged', () => {});
          card.addEventListener('postalCodeChanged', () => {});

          // Check if card is complete on any change
          card.addEventListener('submit', () => {});
        }
      }, 100);

    } catch (err) {
      console.error('Failed to initialize card:', err);
      setError('Failed to initialize payment form');
    }
  };

  const handleSubmit = async () => {
    if (!cardRef.current || !consentChecked) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      // Tokenize the card
      const result = await cardRef.current.tokenize();

      if (result.status === 'OK') {
        // Submit to our backend
        const response = await api.post('/recurring-payments/authorize', {
          contact_id: contactId,
          case_id: caseId,
          card_nonce: result.token,
          cardholder_name: cardholderName || null,
          authorization_type: authType,
          consent_text: CONSENT_TEXT
        });

        if (onSuccess) {
          onSuccess(response.data);
        }
        onClose();
      } else {
        let errorMessage = 'Failed to process card. ';
        if (result.errors && result.errors.length > 0) {
          errorMessage += result.errors.map(e => e.message).join(', ');
        }
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Authorization failed:', err);
      setError(err.response?.data?.detail || 'Failed to authorize card. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-virgo-navy-800 rounded-lg p-6 max-w-lg w-full mx-4 border border-virgo-navy-600 shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-virgo-teal-100 flex items-center">
            <CreditCard className="mr-2 text-virgo-teal-400" size={24} />
            Set Up Recurring Payments
          </h2>
          <button onClick={onClose} className="text-virgo-teal-500 hover:text-virgo-teal-300">
            <X size={24} />
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg flex items-start">
            <AlertCircle className="text-red-400 mr-2 flex-shrink-0 mt-0.5" size={18} />
            <span className="text-red-300 text-sm">{error}</span>
          </div>
        )}

        {loading ? (
          <div className="py-12 flex flex-col items-center">
            <Loader2 className="animate-spin text-virgo-teal-400 mb-3" size={32} />
            <p className="text-virgo-teal-300">Loading payment form...</p>
          </div>
        ) : (
          <>
            {/* Cardholder Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                Cardholder Name (Optional)
              </label>
              <input
                type="text"
                value={cardholderName}
                onChange={(e) => setCardholderName(e.target.value)}
                placeholder="Name as it appears on card"
                className="w-full px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100 placeholder-virgo-teal-600 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
              />
            </div>

            {/* Square Card Container */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                Card Information
              </label>
              <div
                id="card-container"
                ref={cardContainerRef}
                className="bg-white rounded-lg p-3 min-h-[50px]"
              />
            </div>

            {/* Authorization Type */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                Authorization Type
              </label>
              <select
                value={authType}
                onChange={(e) => setAuthType(e.target.value)}
                className="w-full px-4 py-2 bg-virgo-navy-900 border border-virgo-navy-600 rounded-lg text-virgo-navy-100 focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
              >
                <option value="both">Retainer & Scheduled Payments</option>
                <option value="retainer">Retainer Replenishment Only</option>
                <option value="scheduled">Scheduled Payments Only</option>
              </select>
            </div>

            {/* Consent Section */}
            <div className="mb-6 p-4 bg-virgo-navy-700 rounded-lg border border-virgo-navy-600">
              <div className="flex items-start mb-3">
                <Shield className="text-virgo-teal-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                <span className="text-sm font-medium text-virgo-teal-200">Authorization Agreement</span>
              </div>
              <div className="text-xs text-virgo-teal-400 whitespace-pre-line mb-4 max-h-32 overflow-y-auto">
                {CONSENT_TEXT}
              </div>
              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  className="mt-1 mr-3 w-4 h-4 rounded bg-virgo-navy-900 border-virgo-navy-500 text-virgo-teal-500 focus:ring-virgo-teal-500"
                />
                <span className="text-sm text-virgo-teal-200">
                  I have read and agree to the authorization terms above
                </span>
              </label>
            </div>

            {/* Security Note */}
            <div className="mb-6 flex items-start text-xs text-virgo-teal-500">
              <Shield className="mr-2 flex-shrink-0" size={14} />
              <span>
                Your card information is securely processed by Square.
                We never store your full card number on our servers.
              </span>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={!consentChecked || submitting}
              className="w-full py-3 bg-virgo-teal-600 text-white rounded-lg font-medium hover:bg-virgo-teal-500 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={20} />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2" size={20} />
                  Authorize Card
                </>
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default PaymentAuthorizationModal;
